import { EventModel } from 'features/events/EventModel';
import { liveEventSelector } from 'features/events/eventsSelectors';
import { useDispatch } from 'react-redux';
import { useEffect, useContext } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useAudio } from 'features/audio/audio';
import { useAppSelector } from 'hooks/redux';
import { useIsEventLive } from '../eventHooks';
import { WebSocketContext } from 'features/hub/hub';
import { navigateToEvent } from '../thunks';

export const useChannelLiveEventNavigation = (event: EventModel | null) => {
  const navigate = useNavigate();
  const liveEvent: EventModel | null = useAppSelector(liveEventSelector);
  const { pathname } = useLocation();
  const { stop, loadAudio } = useAudio();
  const { isCreatorAppWebview, isBrowserEmbed, isListenerAppWebview } =
    useContext(UserAgentContext);
  const currentEventIsLive = useIsEventLive(event);
  const dispatch = useDispatch();
  const channelFallbackEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.fallbackEnabled
  );
  const { subscribeToBroadcast } = useContext(WebSocketContext);

  useEffect(
    function redirectToCurrentLiveEvent() {
      // Only support this on channels with fallback
      if (!channelFallbackEnabled) return;
      // Don't redirect if a scheduled upcoming event and it's not ended
      if (event?.isUpcoming() && !event?.isEnded()) return;
      // Ignore any redirects on the creator apps
      if (isCreatorAppWebview || isBrowserEmbed) return;
      // If going from live event -> Fallback
      // wait for the audio to finish first
      if (liveEvent?.fallback && currentEventIsLive) return;
      // Only navigate if we have a live event and
      // it isn't the current displayed event
      if (liveEvent && event?.id !== liveEvent?.id) {
        const liveEventId = liveEvent.id;
        const liveEventPath = matchPath(`/events/${liveEventId}`, pathname);
        if (liveEventPath) return;

        console.log(
          '[useChannelLiveEventNavigation] - Navigating to next live event'
        );

        dispatch(
          navigateToEvent(
            liveEvent,
            loadAudio,
            stop,
            navigate,
            subscribeToBroadcast,
            isListenerAppWebview
          )
        );
      }
    },
    [
      event,
      liveEvent,
      navigate,
      pathname,
      stop,
      isBrowserEmbed,
      isCreatorAppWebview,
      currentEventIsLive,
      dispatch,
      loadAudio,
      channelFallbackEnabled,
      isListenerAppWebview,
      subscribeToBroadcast
    ]
  );
};
